import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, API } from '../App';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ArrowLeft, Plus, Edit, Trash2, Download, Filter, RotateCcw, Phone, User, DollarSign } from 'lucide-react';
import { DatePickerField } from '../components/DatePickerField';
import { format } from 'date-fns';

const PAYMENT_METHODS = ['Cash', 'MPESA', 'Bank', 'Cheque', 'Other'];

export default function PackagingTeamStatement() {
  const { id: teamId } = useParams();
  const { isAdmin } = useAuth();
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterStart, setFilterStart] = useState(null);
  const [filterEnd, setFilterEnd] = useState(null);
  const [filterFactory, setFilterFactory] = useState('all');
  const [filterPackSize, setFilterPackSize] = useState('all');

  // Payment dialog
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [payDate, setPayDate] = useState(new Date());
  const [payForm, setPayForm] = useState({ amount: '', method: '', transaction_code: '', remarks: '' });

  const [downloading, setDownloading] = useState(false);

  const buildQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    if (filterStart) params.append('start_date', format(filterStart, 'yyyy-MM-dd'));
    if (filterEnd) params.append('end_date', format(filterEnd, 'yyyy-MM-dd'));
    if (filterFactory !== 'all') params.append('factory', filterFactory);
    if (filterPackSize !== 'all') params.append('pack_size', filterPackSize);
    return params;
  }, [filterStart, filterEnd, filterFactory, filterPackSize]);

  const fetchStatement = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildQueryParams();
      const url = `/packaging-teams/${teamId}/statement${params.toString() ? '?' + params.toString() : ''}`;
      const res = await api.get(url);
      setStatement(res.data);
    } catch (e) {
      toast.error('Failed to load statement');
    } finally {
      setLoading(false);
    }
  }, [teamId, buildQueryParams]);

  useEffect(() => {
    fetchStatement();
  }, [fetchStatement]);

  // Build unique pack-size options from records
  const packSizeOptions = useMemo(() => {
    if (!statement) return [];
    return Array.from(new Set(statement.productions.map(p => p.pack_size).filter(Boolean)));
  }, [statement]);

  const lifetime = () => {
    setFilterStart(null); setFilterEnd(null);
    setFilterFactory('all'); setFilterPackSize('all');
  };

  const resetPayForm = () => {
    setEditingPaymentId(null);
    setPayDate(new Date());
    setPayForm({ amount: '', method: '', transaction_code: '', remarks: '' });
  };

  const editPayment = (pay) => {
    setEditingPaymentId(pay.id);
    setPayDate(new Date(pay.date));
    setPayForm({
      amount: pay.amount?.toString() || '',
      method: pay.method || '',
      transaction_code: pay.transaction_code || '',
      remarks: pay.remarks || ''
    });
    setPayDialogOpen(true);
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    if (!payForm.amount || parseFloat(payForm.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    const payload = {
      date: format(payDate, 'yyyy-MM-dd'),
      amount: parseFloat(payForm.amount),
      method: payForm.method || null,
      transaction_code: payForm.transaction_code || null,
      remarks: payForm.remarks || null
    };
    try {
      if (editingPaymentId) {
        await api.put(`/packaging-teams/${teamId}/payments/${editingPaymentId}`, payload);
        toast.success('Payment updated');
      } else {
        await api.post(`/packaging-teams/${teamId}/payments`, payload);
        toast.success('Payment recorded');
      }
      setPayDialogOpen(false);
      resetPayForm();
      fetchStatement();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const deletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await api.delete(`/packaging-teams/${teamId}/payments/${paymentId}`);
      toast.success('Payment deleted');
      fetchStatement();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const params = buildQueryParams();
      const url = `${API}/packaging-teams/${teamId}/statement/pdf${params.toString() ? '?' + params.toString() : ''}`;
      const token = localStorage.getItem('token');
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const dl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dl;
      a.download = `Team_Statement_${(statement?.team?.name || 'team').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(dl);
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  const fmt = (n) => new Intl.NumberFormat('en-KE').format(Number(n || 0).toFixed(2));
  const fmtKES = (n) => new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 0 }).format(n || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B4D3E]"></div>
      </div>
    );
  }

  if (!statement) {
    return <div className="text-center py-8 text-slate-500">Team not found.</div>;
  }

  const { team, productions, payments, summary } = statement;
  const balesByPack = summary.bales_by_pack_size || {};

  return (
    <div className="space-y-6" data-testid="team-statement-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link to="/production">
            <Button variant="ghost" size="sm" className="gap-1"><ArrowLeft className="h-4 w-4" /> Back</Button>
          </Link>
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#1B4D3E]" data-testid="team-name">{team.name}</h1>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-slate-500">
              {team.leader && <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" /> {team.leader}</span>}
              {team.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {team.phone}</span>}
              <Badge variant={team.is_active ? 'default' : 'secondary'}>{team.is_active ? 'Active' : 'Inactive'}</Badge>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadPDF} disabled={downloading} className="gap-2" data-testid="download-team-pdf">
            <Download className="h-4 w-4" /> {downloading ? 'Downloading…' : 'Download PDF'}
          </Button>
          <Button onClick={() => { resetPayForm(); setPayDialogOpen(true); }} className="btn-primary gap-2" data-testid="add-team-payment-btn">
            <Plus className="h-4 w-4" /> Add Payment
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5"><Filter className="h-4 w-4 text-slate-500" /></div>
            <div className="flex items-center gap-2">
              <Label className="text-xs">From:</Label>
              <DatePickerField date={filterStart} onDateChange={setFilterStart} buttonClassName="w-36 text-sm" displayFormat="MMM d, yyyy" placeholder="Start" />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs">To:</Label>
              <DatePickerField date={filterEnd} onDateChange={setFilterEnd} buttonClassName="w-36 text-sm" displayFormat="MMM d, yyyy" placeholder="End" />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Factory:</Label>
              <Select value={filterFactory} onValueChange={setFilterFactory}>
                <SelectTrigger className="w-36 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="Ndumberi">Ndumberi</SelectItem>
                  <SelectItem value="Githunguri">Githunguri</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Pack:</Label>
              <Select value={filterPackSize} onValueChange={setFilterPackSize}>
                <SelectTrigger className="w-28 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {packSizeOptions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button variant="ghost" size="sm" onClick={lifetime} data-testid="lifetime-btn">
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Lifetime
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-emerald-50">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs uppercase tracking-wider text-emerald-700">Total Bales</p>
            <p className="text-2xl font-bold text-emerald-900" data-testid="summary-total-bales">{fmt(summary.total_bales)}</p>
            {Object.keys(balesByPack).length > 0 && (
              <div className="mt-2 space-y-0.5 text-[11px] text-emerald-700">
                {Object.entries(balesByPack).map(([ps, qty]) => (
                  <div key={ps} className="flex justify-between"><span>{ps}</span><span className="font-medium">{fmt(qty)}</span></div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card className="bg-blue-50">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs uppercase tracking-wider text-blue-700">Total Packets</p>
            <p className="text-2xl font-bold text-blue-900">{fmt(summary.total_packets)}</p>
            <p className="text-[11px] text-blue-700 mt-1">Weight: {fmt(summary.total_weight_packed)} kg</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs uppercase tracking-wider text-amber-700">Earned / Paid</p>
            <p className="text-xl font-bold text-amber-900" data-testid="summary-earned">{fmtKES(summary.total_earned)}</p>
            <p className="text-[11px] text-amber-700 mt-1">Paid: {fmtKES(summary.total_paid)}</p>
          </CardContent>
        </Card>
        <Card className={summary.balance >= 0 ? 'bg-rose-50' : 'bg-emerald-50'}>
          <CardContent className="pt-5 pb-4">
            <p className="text-xs uppercase tracking-wider text-slate-700">Balance Outstanding</p>
            <p className={`text-2xl font-bold ${summary.balance >= 0 ? 'text-rose-700' : 'text-emerald-700'}`} data-testid="summary-balance">{fmtKES(summary.balance)}</p>
            <p className="text-[11px] text-slate-500 mt-1">Earned − Paid</p>
          </CardContent>
        </Card>
      </div>

      {/* Production records */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-heading">Production Records ({productions.length})</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Factory</TableHead>
                <TableHead>Pack Size</TableHead>
                <TableHead className="text-right">Wt/Packet</TableHead>
                <TableHead className="text-right">Bales</TableHead>
                <TableHead className="text-right">Packets</TableHead>
                <TableHead className="text-right">Wt Packed</TableHead>
                <TableHead className="text-right">KES/Bale</TableHead>
                <TableHead className="text-right">Earned</TableHead>
                <TableHead>Remarks</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {productions.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center py-6 text-slate-400 italic">No production records for selected period</TableCell></TableRow>
              ) : productions.map(p => (
                <TableRow key={p.id}>
                  <TableCell>{p.date}</TableCell>
                  <TableCell>{p.factory || '-'}</TableCell>
                  <TableCell>{p.pack_size || '-'}</TableCell>
                  <TableCell className="text-right">{p.actual_weight_per_packet ? fmt(p.actual_weight_per_packet) : '-'}</TableCell>
                  <TableCell className="text-right">{p.num_bales ? fmt(p.num_bales) : '-'}</TableCell>
                  <TableCell className="text-right">{p.total_packets ? fmt(p.total_packets) : '-'}</TableCell>
                  <TableCell className="text-right">{p.total_weight_packed ? fmt(p.total_weight_packed) : '-'}</TableCell>
                  <TableCell className="text-right">{p.packaging_cost_per_bale ? fmtKES(p.packaging_cost_per_bale) : '-'}</TableCell>
                  <TableCell className="text-right font-medium">{p.total_amount_earned ? fmtKES(p.total_amount_earned) : '-'}</TableCell>
                  <TableCell className="text-sm max-w-[220px] truncate" title={p.remarks}>{p.remarks || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Payments */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-heading flex items-center gap-2"><DollarSign className="h-4 w-4" /> Payments Made ({payments.length})</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Transaction Code</TableHead>
                <TableHead>Remarks</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400 italic">No payments recorded</TableCell></TableRow>
              ) : payments.map(pay => (
                <TableRow key={pay.id} data-testid={`payment-row-${pay.id}`}>
                  <TableCell>{pay.date}</TableCell>
                  <TableCell className="text-right font-medium">{fmtKES(pay.amount)}</TableCell>
                  <TableCell>{pay.method || '-'}</TableCell>
                  <TableCell className="text-sm">{pay.transaction_code || '-'}</TableCell>
                  <TableCell className="text-sm max-w-[220px] truncate" title={pay.remarks}>{pay.remarks || '-'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => editPayment(pay)}><Edit className="h-4 w-4" /></Button>
                      {isAdmin && (
                        <Button variant="ghost" size="sm" onClick={() => deletePayment(pay.id)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Payment dialog */}
      <Dialog open={payDialogOpen} onOpenChange={(open) => { setPayDialogOpen(open); if (!open) resetPayForm(); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPaymentId ? 'Edit Payment' : 'Record Payment'} — {team.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitPayment} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Date *</Label>
                <DatePickerField date={payDate} onDateChange={setPayDate} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Amount (KES) *</Label>
                <Input type="number" value={payForm.amount} onChange={e => setPayForm({ ...payForm, amount: e.target.value })} placeholder="0" data-testid="team-payment-amount" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Method</Label>
                <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                  <SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Transaction Code</Label>
                <Input value={payForm.transaction_code} onChange={e => setPayForm({ ...payForm, transaction_code: e.target.value })} placeholder="e.g., RGT123" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Remarks</Label>
              <Input value={payForm.remarks} onChange={e => setPayForm({ ...payForm, remarks: e.target.value })} placeholder="Optional" />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={() => setPayDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-primary" data-testid="save-team-payment-btn">{editingPaymentId ? 'Update' : 'Record'} Payment</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
